<template>
    <div class="flex flex-col h-full select-none text-xs">

        <!-- Header -->
        <div class="flex items-center justify-between px-2 py-1 border-b border-white/10 shrink-0">
            <span class="text-slate-400">Bookmarks</span>
            <span class="text-slate-500">{{ totalFormatted }}</span>
        </div>

        <!-- Storage warning -->
        <div v-if="storageWarning"
             class="px-2 py-1 text-amber-400 bg-amber-900/20 border-b border-amber-700/30 shrink-0 text-xs">
            ⚠ {{ storageWarning }}
        </div>

        <!-- Add bookmark form -->
        <div class="flex gap-1 px-2 py-1.5 border-b border-white/10 shrink-0">
            <input
                v-model="newName"
                @keydown.enter="add"
                placeholder="Bookmark name…"
                class="flex-1 bg-slate-700 border border-white/10 text-slate-200 rounded px-1.5 py-0.5 text-xs placeholder-slate-500 min-w-0"
            />
            <button
                @click="add"
                :disabled="!newName.trim()"
                class="shrink-0 px-2 py-0.5 rounded bg-blue-600/40 hover:bg-blue-600/60 text-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >⭐</button>
        </div>

        <!-- Empty state -->
        <div v-if="bookmarks.length === 0"
             class="flex-1 flex items-center justify-center text-slate-500">
            No bookmarks yet
        </div>

        <!-- Bookmark list -->
        <div v-else class="flex-1 overflow-y-auto hide-scrollbar py-1 min-h-0">
            <div
                v-for="b in sortedBookmarks" :key="b.id"
                @click="selectBookmark(b)"
                class="mx-1 px-2 py-1.5 rounded cursor-pointer transition-colors"
                :class="selected?.id === b.id ? 'bg-blue-600/25' : 'hover:bg-white/5'"
            >
                <div class="flex items-center gap-1.5 min-w-0">
                    <span>{{ b.protected ? '🔒' : '⭐' }}</span>
                    <span class="flex-1 truncate text-slate-200">{{ b.name }}</span>
                    <span class="shrink-0 text-slate-500">{{ formatBytes(b.compressedSizeBytes) }}</span>
                </div>
                <div class="text-slate-600 mt-0.5" style="font-size: 10px;">
                    {{ formatDate(b.timestamp) }}
                    <span v-if="b.description" class="ml-1 text-slate-500">· {{ b.description }}</span>
                </div>
            </div>
        </div>

        <!-- Selected bookmark actions -->
        <div v-if="selected" class="border-t border-white/10 px-2 py-1.5 space-y-1 shrink-0">
            <!-- Rename inline -->
            <div v-if="renaming" class="flex gap-1">
                <input
                    v-model="renameValue"
                    @keydown.enter="commitRename"
                    @keydown.escape="renaming = false"
                    class="flex-1 bg-slate-700 border border-white/10 text-slate-200 rounded px-1.5 py-0.5 text-xs min-w-0"
                    autofocus
                />
                <button @click="commitRename" class="text-blue-300 px-1">✓</button>
                <button @click="renaming = false" class="text-slate-500 px-1">✕</button>
            </div>

            <!-- Action buttons -->
            <div v-else class="flex flex-wrap gap-1">
                <button @click="$emit('restore', selected.id)"
                        class="flex-1 text-center py-0.5 rounded bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 transition-colors">
                    🔄 Restore
                </button>
                <button @click="startRename"
                        class="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors">
                    ✏️
                </button>
                <button @click="toggleProtect"
                        class="px-2 py-0.5 rounded transition-colors"
                        :class="selected.protected ? 'bg-amber-700/40 text-amber-300' : 'bg-slate-700 hover:bg-slate-600 text-slate-300'">
                    🔒
                </button>
                <button @click="remove"
                        :disabled="selected.protected"
                        class="px-2 py-0.5 rounded bg-red-900/30 hover:bg-red-900/50 text-red-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    🗑
                </button>
            </div>

            <!-- Storage info -->
            <div class="text-slate-600 flex gap-3" style="font-size: 10px;">
                <span>orig: {{ formatBytes(selected.originalSizeBytes) }}</span>
                <span>compressed: {{ formatBytes(selected.compressedSizeBytes) }}</span>
                <span>ratio: {{ selected.compressionRatio }}%</span>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useBookmarkStore } from '../history/bookmarkStore';
import { formatBytes } from '../history/bookmarkCompression';

const props = defineProps({
    currentDesignBytes: { type: Number, default: 0 },
});
const emit = defineEmits(['restore', 'create']);

const store    = useBookmarkStore();
const bookmarks = computed(() => store.bookmarks);
const sortedBookmarks = computed(() =>
    [...bookmarks.value].sort((a, b) => b.timestamp - a.timestamp)
);

const totalFormatted = computed(() => store.totalCompressedFormatted);
const storageWarning = computed(() => store.storageWarning(props.currentDesignBytes));

const newName  = ref('');
const selected = ref(null);
const renaming = ref(false);
const renameValue = ref('');

function add() {
    if (!newName.value.trim()) return;
    emit('create', newName.value.trim());
    newName.value = '';
}

function selectBookmark(b) {
    renaming.value = false;
    selected.value = selected.value?.id === b.id ? null : b;
}

function startRename() {
    renameValue.value = selected.value.name;
    renaming.value = true;
}

function commitRename() {
    if (renameValue.value.trim()) {
        store.renameBookmark(selected.value.id, renameValue.value.trim());
        selected.value = bookmarks.value.find(b => b.id === selected.value.id) ?? null;
    }
    renaming.value = false;
}

function toggleProtect() {
    store.setProtected(selected.value.id, !selected.value.protected);
    selected.value = bookmarks.value.find(b => b.id === selected.value.id) ?? null;
}

function remove() {
    const ok = store.deleteBookmark(selected.value.id);
    if (ok) selected.value = null;
}

function formatDate(ts) {
    return new Date(ts).toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}
</script>
